import { Gps, MessageText, DocumentText, Profile2User } from "iconsax-react";
import type { TFunction } from "i18next";
import { Badge } from "@/shared/ui";
import type { BadgeColor } from "@/shared/ui";
import type { AuditEntry, AuditFieldChange } from "@/shared/types/audit";
import type { AuditEntryItemConfig } from "@/shared/components";
import type { Locale } from "@/shared/utils";
import { getLocalizedTargetRepr, formatMoneyKzt } from "@/shared/utils";
import { PROJECT_STATUS_MAP } from "@/features/projects/utils/consts";
import { PLANNED_FIELDS } from "@/features/projects/utils/plannedFields";
import { TASK_STATUS_MAP, PRIORITY_MAP } from "../consts";

const MONEY_FIELD_SET = new Set<string>([...PLANNED_FIELDS, "budget"]);

export const ACTION_ICON_MAP: Record<string, React.ComponentType<{ size: number; color: string }>> = {
  task: Gps,
  taskcomment: MessageText,
  project: DocumentText,
  projectmembership: Profile2User,
};

export function getActionIcon(action: string) {
  const prefix = action.split(".")[0];
  return ACTION_ICON_MAP[prefix] ?? Gps;
}

export function isReorderEntry(entry: AuditEntry): boolean {
  return entry.action === "task.updated" && entry.metadata?.action_context === "reorder";
}

export function getActionLabelKey(entry: AuditEntry): string {
  const isKpi = entry.metadata?.is_kpi === true;

  if (entry.action === "projectmembership.updated" && entry.metadata?.action === "remove_member") {
    return "history.historyAction.projectmembership.updated_remove";
  }

  if (entry.action.startsWith("task.") && isKpi) {
    const suffix = entry.action.slice(5);
    return `history.historyAction.kpi.${suffix}`;
  }

  return `history.historyAction.${entry.action}`;
}

function buildLabelParams(entry: AuditEntry, locale: Locale): Record<string, string> {
  const target = getLocalizedTargetRepr(entry.target_repr, locale);
  if (entry.action.startsWith("projectmembership.")) {
    const member = target.split(" → ")[0] ?? "";
    return { member };
  }
  return { target };
}

export function isFieldChange(value: unknown): value is AuditFieldChange {
  if (!value || typeof value !== "object") return false;
  return "old" in value && "new" in value;
}

export interface TagItem {
  text: string;
  color: string;
}

export function isTagArray(value: unknown): value is TagItem[] {
  if (!Array.isArray(value)) return false;
  return (
    value.length > 0 && typeof value[0] === "object" && value[0] !== null && "text" in value[0] && "color" in value[0]
  );
}

export function renderTags(tags: TagItem[]) {
  if (tags.length === 0) return <span className="text-body-regular-sm content-base-secondary italic">—</span>;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {tags.map((tag) => (
        <Badge key={tag.text} variant="soft" color={tag.color as BadgeColor} text={tag.text} />
      ))}
    </span>
  );
}

export function getStatusBadge(value: string, t: (key: string) => string, tProjects?: (key: string) => string) {
  const taskStatus = TASK_STATUS_MAP[value as keyof typeof TASK_STATUS_MAP];
  if (taskStatus) {
    return <Badge variant="soft" color={taskStatus.color} text={t(taskStatus.label)} />;
  }
  if (tProjects) {
    const projectStatus = PROJECT_STATUS_MAP[value as keyof typeof PROJECT_STATUS_MAP];
    if (projectStatus) {
      return <Badge variant="soft" color={projectStatus.color} text={tProjects(projectStatus.label)} />;
    }
  }
  return <span className="text-body-bold-sm content-base-primary">{value}</span>;
}

export function getPriorityBadge(value: string, t: (key: string) => string) {
  const priorityInfo = PRIORITY_MAP[value as keyof typeof PRIORITY_MAP];
  if (priorityInfo) {
    return <Badge variant="soft" color={priorityInfo.color} text={t(priorityInfo.label)} />;
  }
  return <span className="text-body-bold-sm content-base-primary">{value}</span>;
}

export function getFieldLabel(field: string, t: (key: string) => string): string {
  const key = `history.fieldLabel.${field}`;
  const translated = t(key);
  if (translated !== key) return translated;
  return field;
}

function renderCommentMetadata(entry: AuditEntry): React.ReactNode | null {
  if (entry.action === "taskcomment.created" && entry.metadata && typeof entry.metadata.body === "string") {
    return <p className="text-body-regular-xs content-base-secondary mt-0.5">«{entry.metadata.body}»</p>;
  }

  if (entry.action === "taskcomment.updated" && entry.changes) {
    const bodyChange = (entry.changes as Record<string, unknown>).body;
    if (isFieldChange(bodyChange)) {
      return (
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          <span className="text-body-bold-sm content-base-primary">{String(bodyChange.old ?? "")}</span>
          <span className="text-body-regular-xs content-base-secondary">→</span>
          <span className="text-body-bold-sm content-base-primary">{String(bodyChange.new ?? "")}</span>
        </div>
      );
    }
  }

  if (entry.action === "taskcomment.deleted" && entry.changes && "snapshot" in entry.changes) {
    const snapshot = entry.changes.snapshot as Record<string, unknown>;
    if (snapshot?.body) {
      return <p className="text-body-regular-xs content-base-secondary mt-0.5">«{String(snapshot.body)}»</p>;
    }
  }

  return null;
}

function extractPartnerNames(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) =>
      typeof item === "object" && item !== null ? (item as Record<string, unknown>).partner_name : null,
    )
    .filter((name): name is string => typeof name === "string" && name.length > 0);
}

export function buildTasksAuditConfig(
  t: TFunction,
  tProjects: TFunction,
  locale: Locale,
): AuditEntryItemConfig {
  return {
    actionLabelMap: {},
    iconMap: ACTION_ICON_MAP,
    filteredChangeKeys: ["review_action", "snapshot", "is_active", "assignee_id", "project_picture", "position"],
    getActionLabelKey,
    getLabelParams: (entry) => buildLabelParams(entry, locale),
    renderFieldValue(field, value, _displayValue) {
      if (field === "tags" && isTagArray(value)) return renderTags(value);
      if (field === "status") return getStatusBadge(String(value), t, tProjects);
      if (field === "priority") return getPriorityBadge(String(value), t);
      if (MONEY_FIELD_SET.has(field)) {
        return (
          <span className="text-body-bold-sm content-base-primary">{formatMoneyKzt(String(value ?? ""), locale)}</span>
        );
      }
      if (typeof value === "boolean") {
        const label = value ? t("history.booleanYes") : t("history.booleanNo");
        return <Badge variant="soft" color={value ? "positive" : "gray"} text={label} />;
      }
      return undefined;
    },
    renderFieldChange(field, change, label) {
      if (field !== "partners") return undefined;
      const names = extractPartnerNames(change.new);
      if (!names.length) {
        return (
          <p className="text-body-regular-xs content-base-secondary">
            {label}: <span className="italic">—</span>
          </p>
        );
      }
      return (
        <p className="text-body-regular-xs content-base-secondary">
          {label}: <span className="text-body-bold-sm content-base-primary">{names.join(", ")}</span>
        </p>
      );
    },
    renderMetadata: renderCommentMetadata,
  };
}
